import { Button, useToast } from "@design";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, type WikiFileResponse } from "@/api";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { visibilityOf, withVisibility } from "./frontmatter";
import { useSaveWikiFile, useWikiErrorMessage } from "./useWiki";
import { VisibilityPicker } from "./VisibilityPicker";

/**
 * Edit a page's raw markdown (frontmatter included: this is the human-editable
 * source of truth). Saves name the revision the edit started from; if someone
 * else saved in between, the draft stays on screen so it can be copied, and
 * "Reload" brings in their version.
 */
export function WikiEditor({
  spaceUuid,
  file,
  onDone,
}: Readonly<{ spaceUuid: string; file: WikiFileResponse; onDone: () => void }>) {
  const { t } = useTranslation("knowledge");
  const toast = useToast();
  const queryClient = useQueryClient();
  const message = useWikiErrorMessage();
  const save = useSaveWikiFile(spaceUuid);
  const [conflict, setConflict] = useState(false);

  const form = useAppForm({
    defaultValues: { content: file.content },
    onSubmit: async ({ value }) => {
      try {
        await save.mutateAsync({
          path: { space_uuid: spaceUuid },
          body: { path: file.path, content: value.content, baseRevision: file.revision },
          throwOnError: true,
        });
        toast.success(t("editor.saved"));
        onDone();
      } catch (error) {
        if (error instanceof ApiError && error.code === "WIKI_CONFLICT") setConflict(true);
        else toast.error(message(error, t("editor.saveFailed")));
      }
    },
  });

  const reload = async () => {
    await queryClient.invalidateQueries({
      predicate: (query) => (query.queryKey[0] as { _id?: string } | undefined)?._id === "getWikiFile",
    });
    onDone();
  };

  return (
    <form
      aria-label={t("document.edit")}
      className="flex flex-col gap-4"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      {conflict && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg bg-warning-subtle p-3">
          <Text variant="body-sm" tone="warning" className="min-w-0 flex-1">
            {t("errors.WIKI_CONFLICT")}
          </Text>
          <Button variant="secondary" size="sm" onClick={() => void reload()}>
            {t("editor.reload")}
          </Button>
        </div>
      )}
      <form.Subscribe selector={(s) => s.values.content}>
        {(content) => (
          <VisibilityPicker
            value={visibilityOf(content)}
            canPublish={Boolean(file.canPublish)}
            onChange={(next) => form.setFieldValue("content", withVisibility(content, next))}
          />
        )}
      </form.Subscribe>
      <form.AppField name="content">
        {(field) => <field.TextareaField label={t("editor.label")} rows={22} />}
      </form.AppField>
      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={onDone}>
          {t("editor.cancel")}
        </Button>
        <form.Subscribe selector={(s) => s.isSubmitting}>
          {(submitting) => (
            <Button type="submit" variant="primary" loading={submitting} disabled={conflict}>
              {t("editor.save")}
            </Button>
          )}
        </form.Subscribe>
      </div>
    </form>
  );
}
